import type { IgfdPaymentPlan } from "api-types";

import { useMemo } from "react";
import { useSelector } from "react-redux";

import { BasicPlan } from "@dashboard/environment/plans";

import type { Features, ReduxState } from "../types";

/**
 * A hook that provides access to features of the dashboard app.
 *
 * The features are derived from the restaurant's payment plan,
 * and access is controlled based on payment / flags set in the backend (kitchen)
 */
export const useFeatures: () => Features = () => {
  // We look at multiple slices of the state to derive the feature flags.
  const restaurantState = useSelector((state: ReduxState) => state.restaurant);
  const numMenus = useSelector((state: ReduxState) => state.menu.menus.length);
  const numLocations = useSelector(
    (state: ReduxState) => state.location.locations.length
  );

  return useMemo(() => {
    const selectedRestaurant = restaurantState.selectedRestaurant;
    const paymentPlan: IgfdPaymentPlan =
      restaurantState.paymentPlansByRestaurantUuid[
        selectedRestaurant?.uuid ?? ""
      ] ?? BasicPlan;
    return {
      analytics: paymentPlan.analytics,
      branding: paymentPlan.branding,
      menuFiltering: paymentPlan.menuFiltering,
      products: paymentPlan.products,

      createMenuAllowed: paymentPlan.numMenus > numMenus,
      createLocationAllowed: paymentPlan.numLocations > numLocations,
    } as Features;
  }, [restaurantState, numMenus, numLocations]);
};

/**
 * UI Feature flag set via the admin modal. This is not related to payment plans.
 */
export const useDashboardAppFeatureFlag: (
  flag: keyof ReduxState["dashboardApp"]["featureFlags"]
) => boolean = (flag: keyof ReduxState["dashboardApp"]["featureFlags"]) => {
  const featureFlags = useSelector(
    (state: ReduxState) => state.dashboardApp.featureFlags
  );
  return featureFlags[flag];
};
